import React, { ReactNode, useMemo } from 'react';
import { cn } from './ui/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

/**
 * Vocab highlighter + hover popovers
 * -------------------------------------------------------------
 * - Define source vocab as a list of related surface forms (strings) mapped to a JSX help component.
 * - The code expands that list into a fast lookup and a single combined RegExp for replacement.
 * - The exported <VocabRichText> component replaces matches in a string with a styled span that:
 *   • highlights the term
 *   • shows a help cursor
 *   • reveals a popover on hover with the provided JSX help content
 *
 * Notes
 * - Matching is case-insensitive and respects word boundaries, so "ratio" won't match "ration".
 * - Overlapping matches prefer the longest term (sort by length desc when building regex).
 * - Popover uses Radix UI Tooltip with Portal for proper positioning outside parent containers.
 */

// ---------- Types ----------

export type VocabSourceItem = {
  /** Related surface forms that should all trigger the same help component */
  terms: string[];
  /** The help UI to show for these terms. Can be any ReactNode. */
  render: ReactNode;
};

export type VocabSources = VocabSourceItem[];

export type ExpandedVocab = {
  /** A map from lowercase surface form to its canonical key (first term) */
  surfaceToKey: Map<string, string>;
  /** Lookup from canonical key to the help UI */
  keyToRender: Map<string, ReactNode>;
  /** A single alternation regex matching any term with word boundaries, case-insensitive */
  matcher: RegExp;
};

export type VocabRichTextProps = {
  text: string;
  /** Source definitions: e.g., [{ terms: ["ratio", "ratios"], render: <RatioHelp/> }, ...] */
  sources: VocabSources;
  /** Tailwind classes for highlighted term wrapper */
  highlightClassName?: string;
  /** Optional: control popover open delay (ms) */
  openDelayMs?: number;
  /** Optional: control popover close delay (ms) */
  closeDelayMs?: number;
  /** Optional: callback when vocab help is viewed */
  onVocabViewed?: () => void;
};

// ---------- Utils ----------

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Build an ExpandedVocab from the source list.
 * - Deduplicates terms (case-insensitive)
 * - Sorts by length desc to prefer longer matches (e.g., "unit rate" over "rate")
 * - Creates a single RegExp with Unicode property escapes for robust word boundaries
 */
export function buildExpandedVocab(sources: VocabSources): ExpandedVocab {
  const surfaceToKey = new Map<string, string>();
  const keyToRender = new Map<string, ReactNode>();

  const allTerms: { term: string; key: string }[] = [];

  sources.forEach(({ terms, render }) => {
    if (!terms.length) return;
    const key = terms[0];
    keyToRender.set(key.toLowerCase(), render);
    terms.forEach(t => {
      const lc = t.toLowerCase();
      if (!surfaceToKey.has(lc)) {
        surfaceToKey.set(lc, key.toLowerCase());
        allTerms.push({ term: t, key });
      }
    });
  });

  // Sort terms by length desc to avoid partial-overlap eating (e.g., "proportional" before "ratio")
  allTerms.sort((a, b) => b.term.length - a.term.length);

  // Build a single alternation with escaped literals
  const alternation = allTerms.map(t => escapeRegExp(t.term)).join('|');

  // Word boundaries using Unicode properties:
  // - Ensure we don't match inside longer words using lookarounds for letters/numbers/underscore.
  // - Works for multi-word terms as well.
  const pattern = String.raw`(?<![\p{L}\p{N}_])(${alternation})(?![\p{L}\p{N}_])`;
  const matcher = new RegExp(pattern, 'giu');

  return { surfaceToKey, keyToRender, matcher };
}

// ---------- Popover (hover) ----------

type PopoverProps = {
  anchor: React.ReactNode;
  content: React.ReactNode;
  openDelayMs?: number;
  closeDelayMs?: number;
  onOpen?: () => void;
};

/**
 * Vocab popover using Radix UI Tooltip for proper positioning and animations.
 * This matches the existing tooltip implementation for consistency.
 */
const VocabTooltipProvider: React.FC<{
  delayDuration?: number;
  skipDelayDuration?: number;
  children: ReactNode;
}> = ({ children }) => <TooltipProvider>{children}</TooltipProvider>;

const PopoverHover: React.FC<PopoverProps> = ({ anchor, content, openDelayMs = 120, closeDelayMs = 150, onOpen }) => {
  return (
    <VocabTooltipProvider delayDuration={openDelayMs} skipDelayDuration={closeDelayMs}>
      <Tooltip
        onOpenChange={(open: boolean) => {
          if (open) onOpen?.();
        }}
      >
        <TooltipTrigger asChild>{anchor}</TooltipTrigger>
        <TooltipContent
          sideOffset={8}
          className={cn('tw-z-[10000] tw-w-80 tw-max-w-[84vw]')}
          style={{
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'var(--border)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--spacing-4)',
            backgroundColor: 'white',
            boxShadow: 'var(--elevation-lg)',
          }}
        >
          {content}
        </TooltipContent>
      </Tooltip>
    </VocabTooltipProvider>
  );
};

// ---------- Highlighter ----------

export const VocabRichText: React.FC<VocabRichTextProps> = ({
  text,
  sources,
  highlightClassName = 'tw-bg-yellow-900 tw-ring-1 tw-ring-yellow-300/70 tw-px-0.5 tw-rounded tw-cursor-help',
  openDelayMs,
  closeDelayMs,
  onVocabViewed,
}) => {
  const expanded = useMemo(() => buildExpandedVocab(sources), [sources]);

  // Tokenize + replace with React nodes
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;

  const { matcher, surfaceToKey, keyToRender } = expanded;

  // Iterate all matches
  let matchIndex = 0;
  for (const match of text.matchAll(matcher)) {
    const matchText = match[0];
    const start = match.index ?? 0;
    const end = start + matchText.length;

    if (start > lastIndex) {
      nodes.push(<React.Fragment key={`text-${lastIndex}`}>{text.slice(lastIndex, start)}</React.Fragment>);
    }

    const lc = matchText.toLowerCase();
    const key = surfaceToKey.get(lc) || lc;
    const render = keyToRender.get(key) ?? null;

    const anchor = (
      <span className={highlightClassName} aria-describedby={`help-${key}`}>
        {matchText}
      </span>
    );

    nodes.push(
      <PopoverHover
        key={`match-${matchIndex}-${start}:${end}`}
        anchor={anchor}
        content={render}
        openDelayMs={openDelayMs}
        closeDelayMs={closeDelayMs}
        onOpen={onVocabViewed}
      />,
    );

    lastIndex = end;
    matchIndex++;
  }

  if (lastIndex < text.length) {
    nodes.push(<React.Fragment key={`text-${lastIndex}`}>{text.slice(lastIndex)}</React.Fragment>);
  }

  return <>{nodes}</>;
};

// ---------- Example: ratio-related concepts ----------

// Small helper components for consistent visual style inside popovers
const Term: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
  <div className="tw-mb-2">
    <div
      className="tw-text-gray-900"
      style={{
        fontFamily: 'var(--font-atkinson)',
        fontSize: 'var(--text-xl)',
        fontWeight: 'var(--font-weight-bold)',
        marginBottom: 'var(--spacing-1, 0.25rem)',
      }}
    >
      {title}
    </div>
    {subtitle && (
      <div
        className="tw-text-gray-700"
        style={{
          fontFamily: 'var(--font-atkinson)',
          fontSize: 'var(--text-lg)',
          fontWeight: 'var(--font-weight-normal)',
        }}
      >
        {subtitle}
      </div>
    )}
  </div>
);

const Example: React.FC<{ children: ReactNode }> = ({ children }) => (
  <div
    className="tw-rounded-lg tw-bg-gray-50 tw-p-2 tw-text-gray-800"
    style={{
      marginTop: 'var(--spacing-2, 0.5rem)',
      borderRadius: 'var(--radius)',
      fontFamily: 'var(--font-atkinson)',
      fontSize: 'var(--text-base)',
      fontWeight: 'var(--font-weight-normal)',
    }}
  >
    <span style={{ fontWeight: 'var(--font-weight-bold)' }}>Example:</span> {children}
  </div>
);

export const ratioVocabSources: VocabSources = [
  {
    terms: ['ratio', 'ratios'],
    render: (
      <div id="help-ratio">
        <Term title="Ratio" subtitle="A comparison of two quantities using division" />
        <p
          className="tw-text-gray-600"
          style={{
            fontFamily: 'var(--font-atkinson)',
            fontSize: 'calc(var(--text-base) / 2)',
            fontWeight: 'var(--font-weight-normal)',
            lineHeight: '1.4',
          }}
        >
          Expressed with a colon (a:b), as a fraction (a/b), or with words (&quot;a to b&quot;). Order matters.
        </p>
        <Example>There are 3 red marbles and 5 blue marbles → ratio red:blue = 3:5.</Example>
      </div>
    ),
  },
  {
    terms: ['proportion', 'proportional', 'proportionality'],
    render: (
      <div id="help-proportion">
        <Term title="Proportion" subtitle="An equation stating two ratios are equal" />
        <p
          className="tw-text-gray-600"
          style={{
            fontFamily: 'var(--font-atkinson)',
            fontSize: 'calc(var(--text-base) / 2)',
            fontWeight: 'var(--font-weight-normal)',
            lineHeight: '1.4',
          }}
        >
          If a/b = c/d, then a·d = b·c (cross-products are equal).
        </p>
        <Example>If 3/5 = x/10, then 3·10 = 5·x → x = 6.</Example>
      </div>
    ),
  },
  {
    terms: ['rate', 'rates', 'unit rate', 'unit rates'],
    render: (
      <div id="help-rate">
        <Term title="Rate" subtitle="A ratio comparing two different units" />
        <p
          className="tw-text-gray-600"
          style={{
            fontFamily: 'var(--font-atkinson)',
            fontSize: 'calc(var(--text-base) / 2)',
            fontWeight: 'var(--font-weight-normal)',
            lineHeight: '1.4',
          }}
        >
          A unit rate has a denominator of 1 unit.
        </p>
        <Example>60 miles in 2 hours → 30 miles per hour (unit rate).</Example>
      </div>
    ),
  },
  {
    terms: ['constant of proportionality', 'k'],
    render: (
      <div id="help-k">
        <Term title="Constant of proportionality (k)" subtitle="The fixed multiplier in y = kx" />
        <p
          className="tw-text-gray-600"
          style={{
            fontFamily: 'var(--font-atkinson)',
            fontSize: 'calc(var(--text-base) / 2)',
            fontWeight: 'var(--font-weight-normal)',
            lineHeight: '1.4',
          }}
        >
          When two quantities are proportional, their ratio is constant.
        </p>
        <Example>If y = 2.5x, then k = 2.5 and y/x = 2.5 for all x ≠ 0.</Example>
      </div>
    ),
  },
  {
    terms: ['equivalent ratios', 'equivalent ratio'],
    render: (
      <div id="help-equiv">
        <Term title="Equivalent ratios" subtitle="Ratios that name the same relationship" />
        <p
          className="tw-text-gray-600"
          style={{
            fontFamily: 'var(--font-atkinson)',
            fontSize: 'calc(var(--text-base) / 2)',
            fontWeight: 'var(--font-weight-normal)',
            lineHeight: '1.4',
          }}
        >
          Multiply or divide both parts by the same nonzero number.
        </p>
        <Example>3:5, 6:10, and 9:15 are equivalent ratios.</Example>
      </div>
    ),
  },
  {
    terms: ['simplify', 'simplest form', 'reduce', 'reduced'],
    render: (
      <div id="help-simplify">
        <Term title="Simplify a ratio" subtitle="Divide both terms by their greatest common factor" />
        <Example>12:16 ÷ 4 → 3:4.</Example>
      </div>
    ),
  },
  {
    terms: ['scale factor', 'scaling', 'scaled'],
    render: (
      <div id="help-scale">
        <Term title="Scale factor" subtitle="The multiplier used to resize a ratio or figure" />
        <Example>3:4 scaled by 2 → 6:8 (equivalent ratio).</Example>
      </div>
    ),
  },
  {
    terms: ['part-to-part', 'part to part', 'part-to-whole', 'part to whole'],
    render: (
      <div id="help-part">
        <Term title="Part-to-part vs. part-to-whole" />
        <p
          className="tw-text-gray-600"
          style={{
            fontFamily: 'var(--font-atkinson)',
            fontSize: 'calc(var(--text-base) / 2)',
            fontWeight: 'var(--font-weight-normal)',
            lineHeight: '1.4',
          }}
        >
          Part-to-part compares two parts (red:blue). Part-to-whole compares a part to the total (red:total).
        </p>
        <Example>Red:Blue = 3:5 (part-to-part), Red:Total = 3:8 (part-to-whole).</Example>
      </div>
    ),
  },
  {
    terms: ['colon notation', 'fraction notation'],
    render: (
      <div id="help-notation">
        <Term title="Notation for ratios" />
        <p
          className="tw-text-gray-600"
          style={{
            fontFamily: 'var(--font-atkinson)',
            fontSize: 'calc(var(--text-base) / 2)',
            fontWeight: 'var(--font-weight-normal)',
            lineHeight: '1.4',
          }}
        >
          a:b, a/b, and &quot;a to b&quot; describe the same relationship.
        </p>
      </div>
    ),
  },
  {
    terms: ['cross-multiply', 'cross multiply', 'cross-multiplication'],
    render: (
      <div id="help-cross">
        <Term title="Cross-multiplication" subtitle="Method for solving proportions" />
        <Example>If a/b = c/d, then a·d = b·c → solve for the unknown.</Example>
      </div>
    ),
  },
  {
    terms: ['percent', 'percentage', '%'],
    render: (
      <div id="help-percent">
        <Term title="Percent" subtitle="A ratio per 100" />
        <Example>45% = 45 out of 100 = 45/100 = 9/20.</Example>
      </div>
    ),
  },
  {
    terms: ['odds', 'probability ratio'],
    render: (
      <div id="help-odds">
        <Term title="Odds" subtitle="A ratio of favorable to unfavorable outcomes" />
        <Example>With 1 favorable and 3 unfavorable, odds = 1:3 (probability = 1/4).</Example>
      </div>
    ),
  },
];

// ---------- Minimal usage example ----------

/**
 * Example component. Pass any plain text; matching terms will be highlighted with popovers.
 */
export const Demo: React.FC = () => {
  const sample =
    'In a proportional relationship, the constant of proportionality k makes y = kx. ' +
    'You can compare equivalent ratios, compute a unit rate, and simplify to simplest form.';

  return (
    <div className="tw-prose tw-max-w-none">
      <p>
        <VocabRichText text={sample} sources={ratioVocabSources} />
      </p>
    </div>
  );
};

// ---------- Integration notes ----------
// - Works in any React/TSX codebase. Tailwind classes are optional; adjust highlightClassName as needed.
// - If you must avoid RegExp lookbehind (older runtimes), replace the matcher with a manual scan using \b and custom checks.
// - For SSR, guard matchAll usage or run only on client.
